import { SearchableSelect } from '@/components/SearchableSelect'
import {
  optionsFrom,
  useCities,
  useCountries,
  useProvinces,
  useReligions,
} from './queries'
import { GENDER, JOB_PREFERENCES, MARITAL_STATUS, type PersonalInfoState } from './types'

/**
 * Port of Views/Profile/Information/_Personalinformation.cshtml.
 *
 * Field ids, labels and required markers are kept so the template CSS and any
 * muscle memory still line up.
 *
 * Age is derived from the date of birth rather than typed — the original had it
 * `readonly` and computed it in _personalinformation.js, so this is the same
 * behaviour with the arithmetic in one place.
 */
interface Props {
  value: PersonalInfoState
  onChange: (patch: Partial<PersonalInfoState>) => void
  showErrors: boolean
}

export function PersonalInformationSection({ value, onChange, showErrors }: Props): JSX.Element {
  const provinces = useProvinces()
  const cities = useCities()
  const countries = useCountries()
  const religions = useReligions()

  const invalid = (condition: boolean): string => (showErrors && condition ? ' is-invalid' : '')

  function setDateOfBirth(date: string): void {
    onChange({ DateOfBirth: date, Age: date ? String(ageFrom(date)) : '' })
  }

  return (
    <form className="needs-validation" id="frmPersonalInfo" noValidate>
      <div className="mb-3">
        <label className="form-label">
          Full Name <span className="required">*</span>
        </label>
        <input
          type="text"
          className={`form-control${invalid(!value.FullName.trim())}`}
          id="txtFullName"
          maxLength={200}
          placeholder="Full Name (Max. 200 Character)"
          required
          value={value.FullName}
          onChange={(e) => onChange({ FullName: e.target.value })}
        />
      </div>

      <div className="row">
        <div className="mb-3 col-md-6">
          <label className="form-label">Email</label>
          {/* Read-only, as in the original: the address is the account's, and the
              server takes it from the session regardless of what is sent. */}
          <input
            type="email"
            className="form-control"
            id="txtEmail"
            placeholder="email@example.com"
            readOnly
            required
            value={value.Email}
          />
        </div>
        <div className="mb-3 col-md-6">
          <label className="form-label">
            Phone Number <span className="required">*</span>
          </label>
          <div className="input-group">
            <input
              type="tel"
              className={`form-control mob_no${invalid(!value.PhoneNumber.trim())}`}
              id="phoneNumber"
              placeholder="8999-999-999"
              required
              value={value.PhoneNumber}
              onChange={(e) => onChange({ PhoneNumber: e.target.value })}
            />
          </div>
        </div>
      </div>

      <div className="row">
        <div className="mb-3 col-md-4">
          <label className="form-label">
            Place of Birth <span className="required">*</span>
          </label>
          <input
            type="text"
            className={`form-control${invalid(!value.PlaceOfBirth.trim())}`}
            id="txtPlaceOfBirth"
            maxLength={50}
            placeholder="Place of Birth (Max. 50 Character)"
            required
            value={value.PlaceOfBirth}
            onChange={(e) => onChange({ PlaceOfBirth: e.target.value })}
          />
        </div>
        <div className="mb-3 col-md-4">
          <label className="form-label">
            Date of Birth <span className="required">*</span>
          </label>
          <div className="input-group">
            {/* bootstrap-datepicker replaced with the native date input: same
                value, keyboard accessible, and one less jQuery plugin. */}
            <input
              type="date"
              className={`form-control${invalid(!value.DateOfBirth)}`}
              id="dpDateOfBirth"
              required
              value={value.DateOfBirth}
              onChange={(e) => setDateOfBirth(e.target.value)}
            />
          </div>
        </div>
        <div className="mb-3 col-md-4">
          <label className="form-label">Age</label>
          <input type="number" className="form-control" id="txtAge" readOnly value={value.Age} />
        </div>
      </div>

      <div className="row">
        <div className="mb-3 col-md-4">
          <label className="form-label">
            Domicile <span className="required">*</span>
          </label>
          <SearchableSelect
            id="slsDomicile"
            options={optionsFrom(
              provinces.data?.Data,
              (p) => p.ProvinceID,
              (p) => p.ProvinceName,
            )}
            value={value.DomicileId}
            onChange={(id) => {
              const name =
                provinces.data?.Data?.find((p) => String(p.ProvinceID) === id)?.ProvinceName ?? ''
              onChange({ DomicileId: id, DomicileName: name })
            }}
            placeholder="Select your current domicile"
            emptyLabel={null}
            className={invalid(!value.DomicileId)}
            loading={provinces.isLoading}
          />
        </div>
        <div className="mb-3 col-md-4">
          <label className="form-label">
            City <span className="required">*</span>
          </label>
          <SearchableSelect
            id="slsCity"
            options={optionsFrom(
              cities.data?.Data,
              (c) => c.City_HCIS_ID,
              (c) => c.CityName,
            )}
            value={value.CityId}
            onChange={(id) => onChange({ CityId: id })}
            placeholder="Select your city"
            emptyLabel={null}
            className={invalid(!value.CityId)}
            loading={cities.isLoading}
          />
        </div>
        <div className="mb-3 col-md-4">
          <label className="form-label">
            Country <span className="required">*</span>
          </label>
          <SearchableSelect
            id="slsCountry"
            options={optionsFrom(
              countries.data?.Data,
              (c) => c.Country_HCIS_Code,
              (c) => c.CountryName,
            )}
            value={value.CountryCode}
            onChange={(code) => onChange({ CountryCode: code })}
            placeholder="Select your country"
            emptyLabel={null}
            className={invalid(!value.CountryCode)}
            loading={countries.isLoading}
          />
        </div>
      </div>

      <div className="mb-3">
        <label className="form-label">
          Address <span className="required">*</span>
        </label>
        <input
          type="text"
          className={`form-control${invalid(!value.Address.trim())}`}
          id="txtAddress"
          placeholder="Enter your current address"
          required
          value={value.Address}
          onChange={(e) => onChange({ Address: e.target.value })}
        />
      </div>

      <div className="mb-3">
        <label className="form-label">
          Job Preferences <span className="required">*</span>
        </label>
        <select
          id="slsJobPreferences"
          className={`form-select${invalid(!value.JobPreferences)}`}
          required
          value={value.JobPreferences}
          onChange={(e) => {
            const label = JOB_PREFERENCES.find((p) => p.value === e.target.value)?.label ?? ''
            onChange({ JobPreferences: e.target.value, JobPreferencesLabel: label })
          }}
        >
          <option value="" disabled>
            Select your job preferences
          </option>
          {JOB_PREFERENCES.map((preference) => (
            <option key={preference.value} value={preference.value}>
              {preference.label}
            </option>
          ))}
        </select>
      </div>

      <div className="mb-3">
        <label className="form-label">
          Religion <span className="required">*</span>
        </label>
        <SearchableSelect
          id="slsReligion"
          options={optionsFrom(
            religions.data?.Data,
            (r) => r.ReligionCode,
            (r) => r.ReligionName,
          )}
          value={value.ReligionCode}
          onChange={(code) => onChange({ ReligionCode: code })}
          placeholder="Select your religion"
          emptyLabel={null}
          className={invalid(!value.ReligionCode)}
          loading={religions.isLoading}
        />
      </div>

      <div className="mb-1">
        <label className="form-label">
          Gender <span className="required">*</span>
        </label>
      </div>
      <div className="mb-3">
        {[
          { id: 'cbMale', label: 'Male', value: GENDER.Male },
          { id: 'cbFemale', label: 'Female', value: GENDER.Female },
        ].map((option) => (
          <div className="form-check form-check-inline" key={option.id}>
            <input
              className="form-check-input"
              type="radio"
              name="Gender"
              id={option.id}
              checked={value.Gender === option.value}
              onChange={() => onChange({ Gender: option.value })}
            />
            <label className="form-check-label"> {option.label} </label>
          </div>
        ))}
      </div>

      <div className="mb-3">
        <label className="form-label">
          Marital Status <span className="required">*</span>
        </label>
        <div className="row">
          {/* On-screen order is Single / Divorced / Married / Widow, but the ids
              map to 1 / 3 / 2 / 4 — see MARITAL_STATUS. */}
          {[
            { id: 'cbSingle', label: 'Single', value: MARITAL_STATUS.Single },
            { id: 'cbDivorced', label: 'Divorced', value: MARITAL_STATUS.Divorced },
            { id: 'cbMarried', label: 'Married', value: MARITAL_STATUS.Married },
            { id: 'cbWidow', label: 'Widow / Widower', value: MARITAL_STATUS.Widow },
          ].map((option) => (
            <div className="col-md-3" key={option.id}>
              <div className="form-check">
                <input
                  className="form-check-input"
                  type="radio"
                  name="MaritalStatus"
                  id={option.id}
                  checked={value.MaritalStatusId === option.value}
                  onChange={() => onChange({ MaritalStatusId: option.value })}
                />
                <label className="form-check-label">{option.label}</label>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="mb-3">
        <label className="form-label">Social Media</label>
        <div className="row">
          <div className="col-md-6">
            <input
              type="text"
              className="form-control"
              id="txtLinkedIn"
              placeholder="LinkedIn username"
              value={value.LinkedIn}
              onChange={(e) => onChange({ LinkedIn: e.target.value })}
            />
          </div>
          <div className="col-md-6">
            <input
              type="text"
              className="form-control"
              id="txtInstagram"
              placeholder="Instagram username"
              value={value.Instagram}
              onChange={(e) => onChange({ Instagram: e.target.value })}
            />
          </div>
        </div>
      </div>
    </form>
  )
}

/** Whole years elapsed, which is what the original computed for `#txtAge`. */
function ageFrom(dateOfBirth: string): number {
  const born = new Date(dateOfBirth)
  if (Number.isNaN(born.getTime())) return 0
  const today = new Date()
  let age = today.getFullYear() - born.getFullYear()
  const monthDelta = today.getMonth() - born.getMonth()
  if (monthDelta < 0 || (monthDelta === 0 && today.getDate() < born.getDate())) age--
  return Math.max(age, 0)
}
